import { CUSTOM_SCHEME_PATH } from 'services/api-constants';
import { useClient } from 'urql';
import useLogSentryWithContext from './useLogSentryWithContext';

/**
 * Helper function to extract the operation from a GQL URL string
 * Takes a URL string (e.g., "gql:query { ... }") and returns the operation
 * @param urlString - The URL string to extract the operation from
 * @returns The operation
 */
const findGQLOperation = (
  urlString: string,
): { query: string | undefined; mutation: string | undefined } | undefined => {
  const gqlPrefix = `${CUSTOM_SCHEME_PATH.GQL}:`;
  const trimmedUrl = urlString.trim();

  if (!trimmedUrl) {
    return undefined;
  }

  // Extract operation from URL string (format: "gql:query { ... }" or "gql:mutation { ... }")
  const gqlContent = trimmedUrl.startsWith(gqlPrefix) ? trimmedUrl.substring(gqlPrefix.length).trim() : trimmedUrl;

  if (!gqlContent) {
    return undefined;
  }

  if (gqlContent.startsWith('query ')) {
    return { query: gqlContent, mutation: undefined };
  }

  if (gqlContent.startsWith('mutation ')) {
    return { query: undefined, mutation: gqlContent };
  }

  return undefined;
};

/**
 * Helper function to extract the string value (i.e., the URL) from a GraphQL query/mutation response.
 *
 * Note: Query/mutation operations for file downloads should always return a string URL.
 *
 * @param obj - The GraphQL response data (can be a string or an object)
 * @returns The extracted URL string, or null if no string value is found
 */
const findGQLStringResponse = (obj: any): string | null => {
  // Case 1: Response is already a string - return it directly
  if (typeof obj === 'string') {
    return obj;
  }

  // Case 2: Response is an object - try to extract the string from the first property
  if (obj && typeof obj === 'object') {
    // Get the keys of the object
    const keys = Object.keys(obj);
    if (keys.length > 0) {
      // Get the value of the first property in the object
      const value = obj[keys[0]];
      // If that value is a string, return it (this handles cases like { displayObject: "url" })
      if (typeof value === 'string') {
        return value;
      }
    }
  }

  // No string value found in the response
  return null;
};

/**
 * Hook to handle GQL operations
 * @returns The handleGQLOperation function
 */
const useHandleGQLOperation = () => {
  const client = useClient();
  const logError = useLogSentryWithContext();

  /**
   * Handles a GQL operation
   * @param urlString - The custom scheme URL string to handle (e.g., "gql:query { ... }")
   * @returns The resolved URL string if successful, null if unsuccessful
   */
  const handleGQLOperation = async (urlString: string): Promise<{ url: string | null; errorMessage?: string }> => {
    const operations = findGQLOperation(urlString);

    if (!operations) {
      logError(new Error('No operations found in URL'), {
        extra: {
          gqlUrl: urlString,
          message: 'Failed to get link: No operations found in URL',
        },
      });
      return { url: null };
    }

    const { query, mutation } = operations;

    const result = query
      ? await client.query(query, undefined).toPromise()
      : await client.mutation(mutation!, undefined).toPromise();

    if (result.error?.message) {
      // Caller (e.g. useHandleFileDownload) logs to Sentry with gqlUrl when resolution fails.
      return { url: null, errorMessage: result.error.message };
    }

    if (!result.data) {
      logError(new Error('No data in response'), {
        extra: {
          gqlUrl: urlString,
          message: 'Failed to get link: No data returned from gql operation',
        },
      });
      return { url: null };
    }

    const gqlResponseURL = findGQLStringResponse(result.data);
    if (!gqlResponseURL) {
      logError(new Error('No URL string found in result data'), {
        extra: {
          gqlUrl: urlString,
          message: 'Failed to get link: No string value found in response data',
        },
      });
      return { url: null };
    }

    return { url: gqlResponseURL };
  };

  return { handleGQLOperation };
};

export default useHandleGQLOperation;
